// Session Editor canvases (design §7): the squad heatmap and the speed trace share one time axis. Canvas, not
// Recharts: 20 players × ~2,700 buckets is too heavy for SVG, and dragging needs pointer events. Both draw from the
// trace endpoint's buckets ({ from, bucketMs, players: [{ sessionPlayerId, max, mean, noPos }] }, speed only).
// Pointer: drag a handle to move a boundary, drag anywhere else to zoom into that stretch, click (heatmap) to select a
// player, double-click to zoom out; Esc cancels a zoom box.
import React, { useEffect, useRef, useState } from 'react';
import { clock, zoneOf, toMs } from './seShared';

export const LEFT = 150;
export const PAD = 8;
const HANDLE_PX = 6;
const DRAG_MIN_PX = 4;

const xOf = (t, w, [v0, v1]) => LEFT + (t - v0) / (v1 - v0) * (w - LEFT - PAD);
const tOf = (x, w, [v0, v1]) => v0 + (x - LEFT) / (w - LEFT - PAD) * (v1 - v0);

// Draws on every render at the canvas's CSS width (ResizeObserver), with the backing store scaled for the display.
function useCanvas(height, draw) {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const c = ref.current; if (!c) return undefined;
    const measure = () => setW(c.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure); ro.observe(c);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const c = ref.current; if (!c || !w) return;
    const g = c.getContext && c.getContext('2d'); if (!g) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(w * dpr); c.height = Math.round(height * dpr); c.style.height = `${height}px`;
    g.setTransform(dpr, 0, 0, dpr, 0, 0); g.clearRect(0, 0, w, height);
    const st = getComputedStyle(c); const css = name => st.getPropertyValue(name).trim() || '#888';
    draw(g, w, height, css);
  });
  return [ref, w];
}

// Pointer handling shared by both canvases.
function usePointer(ref, w, view, { handles = [], onDrag, onDragEnd, onZoom, onClick, onHover, onLeave, onZoomOut }) {
  const st = useRef(null);
  const [box, setBox] = useState(null);
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape' && st.current && st.current.mode === 'box') { st.current = { mode: 'cancelled' }; setBox(null); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);
  const pos = e => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const clampT = t => Math.min(view[1], Math.max(view[0], t));
  return {
    box,
    handlers: {
      onPointerDown: e => {
        if (!w) return;
        const [x, y] = pos(e);
        const h = handles.find(hh => Math.abs(xOf(hh.t, w, view) - x) <= HANDLE_PX);
        if (h) st.current = { mode: 'drag', key: h.key };
        else if (x >= LEFT) { st.current = { mode: 'box', x0: x, y0: y }; setBox({ x0: x, x1: x }); }
        else { st.current = null; return; }
        if (ref.current.setPointerCapture) ref.current.setPointerCapture(e.pointerId);
      },
      onPointerMove: e => {
        if (!w) return;
        const [x, y] = pos(e);
        const s = st.current;
        if (s && s.mode === 'drag') onDrag && onDrag(s.key, clampT(tOf(x, w, view)));
        else if (s && s.mode === 'box') setBox({ x0: s.x0, x1: x });
        if (!s || s.mode !== 'drag') onHover && onHover(tOf(x, w, view), x, y);
        else onHover && onHover(null);
      },
      onPointerUp: e => {
        const s = st.current; st.current = null;
        if (!s || !w) return;
        const [x] = pos(e);
        if (s.mode === 'drag') { onDragEnd && onDragEnd(s.key); return; }
        if (s.mode !== 'box') return;
        setBox(null);
        if (Math.abs(x - s.x0) > DRAG_MIN_PX) onZoom && onZoom(clampT(tOf(Math.min(x, s.x0), w, view)), clampT(tOf(Math.max(x, s.x0), w, view)));
        else onClick && onClick(tOf(s.x0, w, view), s.x0, s.y0);
      },
      onPointerLeave: () => { if (!st.current) onLeave && onLeave(); },
      onDoubleClick: () => onZoomOut && onZoomOut(),
    },
  };
}

// Buckets behind pixel column cx: [b0, b1) of the trace arrays.
function columnBuckets(trace, w, view, cx) {
  const cols = Math.max(1, Math.floor(w - LEFT - PAD));
  const t0 = view[0] + cx / cols * (view[1] - view[0]); const t1 = view[0] + (cx + 1) / cols * (view[1] - view[0]);
  const from = toMs(trace.from);
  const b0 = Math.floor((t0 - from) / trace.bucketMs); const b1 = Math.max(b0 + 1, Math.ceil((t1 - from) / trace.bucketMs));
  return [b0, b1];
}
// Pixel columns whose middle lies in one of the stretches (match days: a player's excluded stretches)
function columnsIn(stretches, w, view) {
  const cols = Math.max(1, Math.floor(w - LEFT - PAD)); const out = new Uint8Array(cols);
  if (!stretches || !stretches.length) return out;
  for (let cx = 0; cx < cols; cx++) { const t = view[0] + (cx + 0.5) / cols * (view[1] - view[0]); if (stretches.some(([a, b]) => t >= a && t < b)) out[cx] = 1; }
  return out;
}
// One value per pixel column: 'max' (selected player), 'mean' (all players), plus the column's state.
function columnSeries(trace, tp, w, view, kind) {
  const cols = Math.max(1, Math.floor(w - LEFT - PAD));
  const vals = new Float32Array(cols).fill(NaN); const state = new Uint8Array(cols).fill(2);   // 0 data, 1 no position, 2 none
  if (!trace || !tp) return { vals, state, cols };
  const n = tp.max.length;
  for (let cx = 0; cx < cols; cx++) {
    const [b0, b1] = columnBuckets(trace, w, view, cx);
    let m = -1; let sum = 0; let k = 0; let st = 2;
    for (let b = Math.max(0, b0); b < Math.min(n, b1); b++) {
      if (tp.max[b] == null) continue;
      st = Math.min(st, tp.noPos[b] ? 1 : 0);
      if (tp.max[b] > m) m = tp.max[b];
      sum += tp.mean[b]; k++;
    }
    if (k) { vals[cx] = kind === 'max' ? m : sum / k; state[cx] = st; }
  }
  return { vals, state, cols };
}

function timeAxis(g, w, y, view, css) {
  g.fillStyle = css('--text-muted'); g.font = '11px Inter, system-ui, sans-serif'; g.textAlign = 'center';
  const span = (view[1] - view[0]) / 1000; const fit = Math.max(2, (w - LEFT - PAD) / 75);
  const step = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800].find(x => span / x <= fit) || 3600;
  const first = Math.ceil(view[0] / 1000 / step) * step * 1000;
  for (let t = first; t <= view[1]; t += step * 1000) g.fillText(clock(t).slice(0, step < 60 ? 8 : 5), xOf(t, w, view), y);
}
function vline(g, x, top, bottom, color, width = 1, dash = null) {
  g.save(); g.strokeStyle = color; g.lineWidth = width; if (dash) g.setLineDash(dash);
  g.beginPath(); g.moveTo(x, top); g.lineTo(x, bottom); g.stroke(); g.restore();
}
function drawHandles(g, w, view, top, bottom, win, color, { dashed = false, grips = false } = {}) {
  if (!win) return;
  for (const t of [win[0], win[1]]) {
    const x = xOf(t, w, view); if (x < LEFT - 1 || x > w - PAD + 1) continue;
    vline(g, x, top, bottom, color, 2, dashed ? [5, 4] : null);
    if (grips) { g.fillStyle = color; g.fillRect(x - 4, top, 8, 14); }
  }
}
function drawBox(g, w, view, box, top, bottom, css) {
  if (!box) return;
  const x0 = Math.min(box.x0, box.x1); const x1 = Math.max(box.x0, box.x1);
  g.save(); g.fillStyle = css('--se-accent'); g.globalAlpha = 0.14; g.fillRect(x0, top, x1 - x0, bottom - top);
  g.globalAlpha = 1; g.strokeStyle = css('--se-accent'); g.setLineDash([4, 3]); g.strokeRect(x0 + 0.5, top + 0.5, x1 - x0 - 1, bottom - top - 1);
  const a = tOf(x0, w, view); const b = tOf(x1, w, view); const right = x1 > w - 170;
  g.fillStyle = css('--text-primary'); g.font = '600 11px Inter, system-ui, sans-serif'; g.textAlign = right ? 'right' : 'left';
  g.fillText(`${clock(a)}–${clock(b)} (${Math.round((b - a) / 1000)} s)`, right ? x0 - 4 : x1 + 4, top + 14);
  g.restore();
}
// The drill hovered in the period list (like Sonra's drill timeline): a grey band with its name; mode 'outline' (heatmap)
// draws a dashed frame over the coloured rows instead of filling them.
function drawHighlight(g, w, view, hl, top, bottom, css, mode = 'band') {
  if (!hl) return;
  const a = Math.max(LEFT, xOf(hl.start, w, view)); const b = Math.min(w - PAD, xOf(hl.end, w, view));
  if (!(b > a)) return;
  g.save();
  if (mode === 'outline') { g.fillStyle = 'rgba(255,255,255,0.10)'; g.fillRect(a, top, b - a, bottom - top); g.strokeStyle = css('--text-primary'); g.lineWidth = 1.5; g.setLineDash([5, 3]); g.strokeRect(a + 0.5, top + 0.5, b - a - 1, bottom - top - 1); }
  else { g.fillStyle = 'rgba(128,128,128,0.20)'; g.fillRect(a, top, b - a, bottom - top); }
  g.setLineDash([]); g.font = '600 11px Inter, system-ui, sans-serif'; g.textAlign = 'center';
  const label = hl.name.length > 40 ? `${hl.name.slice(0, 39)}…` : hl.name; const tw = g.measureText(label).width + 10; const cx = Math.min(Math.max((a + b) / 2, LEFT + tw / 2), w - PAD - tw / 2);
  g.fillStyle = css('--bg-card'); g.fillRect(cx - tw / 2, top + 1, tw, 15); g.fillStyle = css('--text-primary'); g.fillText(label, cx, top + 12);
  g.restore();
}
function shade(g, w, view, win, top, bottom, color) {
  if (!win) return;
  const a = Math.max(LEFT, xOf(win[0], w, view)); const b = Math.min(w - PAD, xOf(win[1], w, view));
  if (b > a) { g.fillStyle = color; g.fillRect(a, top, b - a, bottom - top); }
}

// ── Squad heatmap ──────────────────────────────────────────────────────────────────────────────────────────────
// rows: [{ sp, label, selected, excluded, warn, own: [a, b] | null, ownKind: 'own' | 'group', hintTimes: [ms], peak: [a, b] | null }]
export function SquadHeatmap({ trace, rows, view, period, scopeWin, scopeKind, editable, notes, highlight, dragHandles, onDrag, onDragEnd, onZoom, onZoomOut, onSelect, onHover }) {
  const rowH = 14;
  const height = Math.max(60, rows.length * rowH + 28);
  const tpBySp = new Map((trace ? trace.players : []).map(p => [p.sessionPlayerId, p]));
  const [ref, w] = useCanvas(height, (g, W, H, css) => {
    const bottom = H - 20;
    const zone = z => css(`--se-z${z}`);
    rows.forEach((r, i) => {
      const y = 4 + i * rowH;
      g.font = `${r.selected ? '600 ' : ''}11px Inter, system-ui, sans-serif`; g.textAlign = 'right';
      g.fillStyle = r.warn ? css('--se-warn') : r.selected ? css('--se-accent') : css('--text-secondary');
      const label = (r.warn ? '⚠ ' : '') + r.label;
      g.fillText(label.length > 22 ? `${label.slice(0, 21)}…` : label, LEFT - 8, y + rowH * 0.75);
      const { vals, state, cols } = columnSeries(trace, tpBySp.get(r.sp), W, view, 'max');
      for (let cx = 0; cx < cols; cx++) {
        g.fillStyle = state[cx] === 2 ? css('--se-nodata') : state[cx] === 1 ? css('--se-nopos') : zone(zoneOf(vals[cx]));
        g.fillRect(LEFT + cx, y, 1.2, rowH - 1);
      }
      if (r.excluded) { g.fillStyle = 'rgba(128,128,128,0.45)'; g.fillRect(LEFT, y, W - LEFT - PAD, rowH - 1); }
      if (r.own && period) {
        // greyed: the part of the period that does not count for him; his own (purple) or group (teal) edges
        g.fillStyle = 'rgba(128,128,128,0.55)';
        if (r.own[0] > period[0]) g.fillRect(xOf(period[0], W, view), y, xOf(Math.min(r.own[0], period[1]), W, view) - xOf(period[0], W, view), rowH - 1);
        if (r.own[1] < period[1]) g.fillRect(xOf(Math.max(r.own[1], period[0]), W, view), y, xOf(period[1], W, view) - xOf(Math.max(r.own[1], period[0]), W, view), rowH - 1);
        g.fillStyle = css(r.ownKind === 'group' ? '--se-group' : '--se-override');
        for (const t of r.own) g.fillRect(xOf(t, W, view) - 1, y, 2, rowH - 1);
      }
      for (const t of r.hintTimes || []) { g.fillStyle = css('--se-warn'); g.fillRect(xOf(t, W, view) - 1.5, y - 1, 3, rowH + 1); }
      // excluded stretches (accepted GPS spikes): a dark mark on his row, at least 3 px wide
      for (const [ca, cb] of r.cuts || []) { const x1 = xOf(ca, W, view); const x2 = Math.max(x1 + 3, xOf(cb, W, view)); g.fillStyle = css('--text-primary'); g.fillRect(x1, y, x2 - x1, rowH - 1); }
      if (r.peak) { g.save(); g.strokeStyle = css('--text-primary'); g.lineWidth = 2; const a = xOf(r.peak[0], W, view); g.strokeRect(a, y - 1, xOf(r.peak[1], W, view) - a, rowH + 1); g.restore(); }
      for (const n of notes || []) if (n.sp === r.sp) { g.fillStyle = css('--text-primary'); g.beginPath(); const x = xOf(n.at, W, view); g.moveTo(x - 4, y); g.lineTo(x + 4, y); g.lineTo(x, y + 6); g.fill(); }
    });
    shade(g, W, view, period, 0, bottom, css('--se-shade'));
    drawHighlight(g, W, view, highlight, 0, bottom, css, 'outline');
    drawHandles(g, W, view, 0, bottom, period, css('--se-handle'), { grips: editable && scopeKind === 'period' });
    if (scopeKind === 'group' && scopeWin) drawHandles(g, W, view, 0, bottom, scopeWin, css('--se-group'), { grips: editable });
    for (const n of notes || []) if (!n.sp) vline(g, xOf(n.at, W, view), 0, bottom, css('--text-muted'), 1, [2, 3]);
    timeAxis(g, W, H - 6, view, css);
    drawBox(g, W, view, box, 0, bottom, css);
  });
  const { box, handlers } = usePointer(ref, w, view, {
    handles: editable ? dragHandles : [], onDrag, onDragEnd, onZoom, onZoomOut,
    onClick: (t, x, y) => { const i = Math.floor((y - 4) / rowH); if (rows[i]) onSelect(rows[i].sp); },
    onHover: t => onHover && onHover(t), onLeave: () => onHover && onHover(null),
  });
  return (
    <div className="se-canvaswrap">
      <canvas ref={ref} style={{ height }} {...handlers} aria-label="Squad heatmap: rows are players, colour is the speed zone" />
    </div>
  );
}

// ── Speed trace ────────────────────────────────────────────────────────────────────────────────────────────────
// mode 'all': a thin line per included player (mean per column), a bold squad median and the selected player;
// mode 'player': the selected player's peak speed per column. Lines in km/h, zone lines at 19.8 and 25.2.
// Match days (gaps build Stage A): excludedOf(sp) gives a player's excluded stretches (outside his counted periods).
// They are greyed and labelled "excluded" for the selected player, his line is faded there, and the squad median and
// the thin lines leave them out. The axis stays 0–36 km/h; samples are never changed and the tooltip shows the value.
// Stage B: countedOf(sp) gives the stretches counted by hand ("count this stretch"): a green strip along the bottom for
// the selected player, labelled "counted by you"; they are no longer excluded, so nothing is greyed there.
export function SpeedTrace({ trace, view, mode, included, selectedSp, selectedLabel, period, ownWin, groupWin, scopeWin, scopeKind, editable, dragHandles,
  hintTimes, notes, reps, exclusions, peaks, highlight, excludedOf, countedOf, onDrag, onDragEnd, onZoom, onZoomOut, onHover }) {
  const height = 250;
  const [tip, setTip] = useState(null);
  const drawn = useRef(null);
  const tpBySp = new Map((trace ? trace.players : []).map(p => [p.sessionPlayerId, p]));
  const [ref, w] = useCanvas(height, (g, W, H, css) => {
    const top = 6; const bot = H - 22; const yOf = kmh => bot - Math.min(kmh, 36) / 36 * (bot - top);
    g.font = '11px Inter, system-ui, sans-serif'; g.textAlign = 'right';
    for (const [kmh, lbl] of [[0, '0'], [19.8, '19.8'], [25.2, '25.2'], [36, '36 km/h']]) {
      g.save(); g.strokeStyle = kmh === 19.8 ? css('--se-z5') : kmh === 25.2 ? css('--se-z6') : css('--border'); if (kmh && kmh < 36) g.setLineDash([4, 4]);
      g.beginPath(); g.moveTo(LEFT, yOf(kmh)); g.lineTo(W - PAD, yOf(kmh)); g.stroke(); g.restore();
      g.fillStyle = css('--text-muted'); g.fillText(lbl, LEFT - 6, yOf(kmh) + 4);
    }
    shade(g, W, view, period, top, bot, css('--se-shade'));
    drawHighlight(g, W, view, highlight, top, bot, css);   // behind the lines
    if (reps && reps.length >= 2) {
      g.save(); g.font = '600 11px Inter, system-ui, sans-serif'; g.textAlign = 'center';
      reps.forEach(([a, b], k) => {
        const x1 = xOf(a, W, view); const x2 = xOf(b, W, view);
        if (x2 - x1 > 18) { g.fillStyle = css('--se-accent'); g.fillText(`R${k + 1}`, (x1 + x2) / 2, top + 28); }
        if (k < reps.length - 1) { g.globalAlpha = 0.08; g.fillStyle = css('--text-primary'); g.fillRect(x2, top, xOf(reps[k + 1][0], W, view) - x2, bot - top); g.globalAlpha = 1; }
      });
      g.restore();
    }
    const own = ownWin || groupWin;
    if (own && period && mode === 'player') {
      for (const [p, q] of [[period[0], Math.min(own[0], period[1])], [Math.max(own[1], period[0]), period[1]]]) {
        if (q <= p) continue;
        const x1 = xOf(p, W, view); const x2 = xOf(q, W, view);
        g.fillStyle = 'rgba(128,128,128,0.22)'; g.fillRect(x1, top, x2 - x1, bot - top);
        if (x2 - x1 > 70) { g.fillStyle = css('--text-muted'); g.textAlign = 'center'; g.fillText('not counted', (x1 + x2) / 2, top + 14); }
      }
    }
    for (const [a, b] of exclusions || []) {
      // excluded stretches (accepted GPS spikes): a band at least 6 px wide with its label; the samples are not changed,
      // so the spike is still drawn, but these seconds are left out of his numbers in this period
      const x1 = xOf(a, W, view); const x2 = Math.max(x1 + 6, xOf(b, W, view));
      if (x2 < LEFT || x1 > W - PAD) continue;
      g.save(); g.fillStyle = css('--se-warn'); g.globalAlpha = 0.35; g.fillRect(x1 - 3, top, x2 - x1 + 6, bot - top);
      g.globalAlpha = 1; g.font = '600 10px Inter, system-ui, sans-serif'; g.textAlign = x1 > W - 150 ? 'right' : 'left'; g.fillStyle = css('--text-primary');
      g.fillText('excluded: GPS spike', x1 > W - 150 ? x1 - 6 : x2 + 6, top + 26); g.restore();
    }
    // keep(cx): which columns this stroke draws (excluded columns are drawn separately, faded)
    const line = (vals, cols, color, width, alpha, keep = null) => {
      g.save(); g.strokeStyle = color; g.lineWidth = width; g.globalAlpha = alpha; g.beginPath(); let pen = false;
      for (let cx = 0; cx < cols; cx++) { const v = vals[cx]; if (Number.isNaN(v) || (keep && !keep(cx))) { pen = false; continue; } const x = LEFT + cx; const y = yOf(v * 3.6); if (pen) g.lineTo(x, y); else { g.moveTo(x, y); pen = true; } }
      g.stroke(); g.restore();
    };
    const exclCols = sp => (excludedOf && sp ? columnsIn(excludedOf(sp), W, view) : null);
    // excluded columns faded, the rest as usual; nothing excluded → one stroke, as before
    const split = (vals, cols, color, width, alpha, ex) => {
      if (!ex || !ex.some(Boolean)) { line(vals, cols, color, width, alpha); return; }
      line(vals, cols, color, width, alpha * 0.25, cx => ex[cx]); line(vals, cols, color, width, alpha, cx => !ex[cx]);
    };
    const selEx = exclCols(selectedSp);
    if (mode === 'player' && selEx && selEx.some(Boolean)) {
      g.save(); g.fillStyle = 'rgba(128,128,128,0.18)'; g.font = '600 10px Inter, system-ui, sans-serif'; g.textAlign = 'center';
      for (let cx = 0; cx < selEx.length;) {
        if (!selEx[cx]) { cx++; continue; }
        let e = cx; while (e < selEx.length && selEx[e]) e++;
        g.fillRect(LEFT + cx, top, e - cx, bot - top);
        if (e - cx > 56) { g.fillStyle = css('--text-muted'); g.fillText('excluded', LEFT + (cx + e) / 2, bot - 6); g.fillStyle = 'rgba(128,128,128,0.18)'; }
        cx = e;
      }
      g.restore();
    }
    if (mode === 'player' && countedOf && selectedSp) {
      g.save(); g.font = '600 10px Inter, system-ui, sans-serif'; g.textAlign = 'center';
      for (const [a, b] of countedOf(selectedSp)) {
        const x1 = Math.max(LEFT, xOf(a, W, view)); const x2 = Math.min(W - PAD, xOf(b, W, view));
        if (x2 <= x1) continue;
        g.fillStyle = css('--se-counted'); g.globalAlpha = 0.8; g.fillRect(x1, bot - 4, x2 - x1, 4); g.globalAlpha = 1;
        if (x2 - x1 > 90) { g.fillStyle = css('--text-muted'); g.fillText('counted by you', (x1 + x2) / 2, bot - 8); }
      }
      g.restore();
    }
    if (mode === 'all') {
      const series = (included || []).map(sp => ({ ...columnSeries(trace, tpBySp.get(sp), W, view, 'mean'), ex: exclCols(sp) }));
      const cols = series.length ? series[0].cols : Math.max(1, Math.floor(W - LEFT - PAD));
      series.forEach((s, i) => { if (included[i] !== selectedSp) line(s.vals, cols, css('--text-muted'), 1, 0.35, s.ex ? cx => !s.ex[cx] : null); });
      const med = new Float32Array(cols).fill(NaN);
      for (let cx = 0; cx < cols; cx++) { const vs = series.filter(s => !(s.ex && s.ex[cx])).map(s => s.vals[cx]).filter(v => !Number.isNaN(v)).sort((a, b) => a - b); if (vs.length) med[cx] = vs[(vs.length - 1) >> 1]; }
      line(med, cols, css('--text-primary'), 2.4, 0.9);
      const mine = selectedSp ? columnSeries(trace, tpBySp.get(selectedSp), W, view, 'mean') : null;
      if (mine) split(mine.vals, cols, css('--se-accent'), 1.6, 1, selEx);
      drawn.current = { mode, med, mine, cols, selEx };
    } else {
      const mine = columnSeries(trace, tpBySp.get(selectedSp), W, view, 'max');
      split(mine.vals, mine.cols, css('--se-accent'), 1.2, 1, selEx);
      drawn.current = { mode, mine, cols: mine.cols, selEx };
    }
    drawHandles(g, W, view, top, bot, period, css('--se-handle'), { grips: editable && scopeKind === 'period' });
    if (ownWin) drawHandles(g, W, view, top, bot, ownWin, css('--se-override'), { dashed: true, grips: editable && scopeKind === 'player' });
    else if (groupWin) drawHandles(g, W, view, top, bot, groupWin, css('--se-group'), { dashed: true });
    if (scopeKind === 'group' && scopeWin) drawHandles(g, W, view, top, bot, scopeWin, css('--se-group'), { grips: editable });
    if (scopeKind === 'player' && !ownWin && scopeWin) drawHandles(g, W, view, top, bot, scopeWin, css('--se-override'), { dashed: true, grips: editable });
    for (const n of notes || []) if (!n.sp || n.sp === selectedSp) vline(g, xOf(n.at, W, view), top, bot, css('--text-muted'), 1, [2, 3]);
    for (const t of hintTimes || []) vline(g, xOf(t, W, view), top, bot, css('--se-warn'), 2, [2, 3]);
    if (peaks && mode === 'player') {
      g.save(); g.font = '10px Inter, system-ui, sans-serif'; g.textAlign = 'left';
      peaks.forEach((p, i) => {
        const x1 = xOf(p.start, W, view); const x2 = xOf(p.end, W, view); const y = top + 42 + i * 13;
        if (x2 < LEFT || x1 > W - PAD) return;
        if (p.marked) { g.globalAlpha = 0.13; g.fillStyle = css('--se-accent'); g.fillRect(x1, top, x2 - x1, bot - top); }
        g.globalAlpha = p.marked ? 1 : 0.6; g.fillStyle = css(p.hsr ? '--se-z5' : '--se-accent'); g.fillRect(x1, y, Math.max(2, x2 - x1), 3);
        g.fillStyle = css('--text-primary'); g.fillText(`${p.label} ${p.value.toFixed(1)}${p.marked ? ` m/min · ${clock(p.start)}–${clock(p.end)}` : ''}`, Math.max(LEFT, x1) + 2, y - 2);
      });
      g.restore();
    }
    if (trace && trace.bucketMs <= 100) { g.fillStyle = css('--text-muted'); g.textAlign = 'right'; g.fillText('10 Hz samples', W - PAD - 4, top + 12); }
    timeAxis(g, W, H - 6, view, css);
    drawBox(g, W, view, box, top, bot, css);
  });
  const hover = (t, x, y) => {
    onHover && onHover(t);
    const d = drawn.current;
    if (t == null || !d || !w || x < LEFT || x > w - PAD) { setTip(null); return; }
    const cx = Math.floor(x - LEFT);
    const [b0, b1] = trace ? columnBuckets(trace, w, view, cx) : [0, 0];
    const from = trace ? toMs(trace.from) : 0; const bucket = trace ? trace.bucketMs : 1000;
    let text;
    const kmh = v => (Number.isNaN(v) || v == null ? null : `${(v * 3.6).toFixed(1)} km/h`);
    if (d.mode === 'player') {
      const tp = tpBySp.get(selectedSp); let best = -1; let at = null; let noPos = false; let any = false;
      if (tp) for (let b = Math.max(0, b0); b < Math.min(tp.max.length, b1); b++) { if (tp.max[b] == null) continue; any = true; noPos = noPos || tp.noPos[b]; if (tp.max[b] > best) { best = tp.max[b]; at = from + b * bucket; } }
      text = !any ? `${clock(t, true)} · no samples` : `${clock(at, bucket < 1000)}${b1 - b0 > 1 ? ` (peak of ${clock(from + b0 * bucket)}–${clock(from + b1 * bucket)})` : ''} · ${(best * 3.6).toFixed(1)} km/h (${best.toFixed(2)} m/s)${noPos ? ' · no position (speed 0)' : ''}`
        + (d.selEx && d.selEx[cx] ? ' · excluded: outside his counted match periods' : '');
    } else {
      const mine = d.mine ? kmh(d.mine.vals[cx]) : null; const med = kmh(d.med[cx]);
      text = `${clock(t)} · ${selectedLabel || 'selected'}: ${mine || 'no samples'} · squad median: ${med || '–'}`;
    }
    setTip({ x, y, text });
  };
  const { box, handlers } = usePointer(ref, w, view, {
    handles: editable ? dragHandles : [], onDrag, onDragEnd, onZoom, onZoomOut, onHover: hover, onLeave: () => { setTip(null); onHover && onHover(null); },
  });
  return (
    <div className="se-canvaswrap">
      <canvas ref={ref} style={{ height }} {...handlers} aria-label="Speed trace" />
      {tip && !box && <div className="se-tip" style={{ left: Math.min(tip.x + 12, (w || 600) - 330), top: Math.max(0, tip.y - 30) }}>{tip.text}</div>}
    </div>
  );
}
